import React, { useEffect, useMemo, useState } from "react";
import { getParcelMenu, createParcelOrder } from "./operationsApi";
import "./parcelOrders.css";

export default function ParcelOrders({ restaurantSlug, onCreated }) {
  const [menu, setMenu] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("INR");

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError("");
    setMenu([]);
    setQuantities({});
    setSelectedCategory("ALL");
    setQuery("");
    setMessage("");
    getParcelMenu(restaurantSlug)
      .then((data) => {
        if (!live) return;
        setMenu(data.items || []);
        setCurrency(data.currency || "INR");
      })
      .catch((e) => { if (live) setError(e.message || "Could not load menu."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [restaurantSlug]);

  // The backend already sorts by category display order, so preserve that order.
  const categories = useMemo(() => [...new Set(menu.map((i) => i.category).filter(Boolean))], [menu]);
  const filtered = useMemo(() => menu.filter((i) => {
    const inCategory = selectedCategory === "ALL" || i.category === selectedCategory;
    const term = query.trim().toLocaleLowerCase();
    return inCategory && (!term || `${i.name} ${i.category}`.toLocaleLowerCase().includes(term));
  }), [menu, selectedCategory, query]);
  const chosen = menu.filter((i) => Number(quantities[i.id]) > 0);
  const total = chosen.reduce((sum, i) => sum + Number(i.price) * Number(quantities[i.id]), 0);
  const count = chosen.reduce((sum, i) => sum + Number(quantities[i.id]), 0);
  const money = (value) => {
    try { return new Intl.NumberFormat("en-IN", { style: "currency", currency }).format(value); }
    catch (_) { return `${currency} ${Number(value).toFixed(2)}`; }
  };
  function changeQuantity(id, next) {
    const n = Math.max(0, Math.min(100, Math.trunc(Number(next) || 0)));
    setQuantities((current) => ({ ...current, [id]: n }));
  }
  async function submit(event) {
    event.preventDefault();
    if (!chosen.length || busy) return;
    setBusy(true); setError(""); setMessage("");
    try {
      const result = await createParcelOrder(restaurantSlug, chosen.map((i) => ({ menu_item_id: i.id, quantity: Number(quantities[i.id]) })));
      setMessage(`Created ${result.order.order_number}. Go to Pending Approval to send it to kitchen.`);
      setQuantities({});
      if (onCreated) {
        try { await onCreated(); } catch (_) { /* Order is already created. */ }
      }
    } catch (err) { setError(err.message || "Unable to create parcel order."); }
    finally { setBusy(false); }
  }
  return (
    <section className="ops-panel parcel-panel">
      <div className="parcel-page-header"><div><div className="parcel-kicker">OPERATIONS / TAKEAWAY</div><h2>New Parcel Order</h2><p>Create a walk-in takeaway order without assigning a table or QR code.</p></div><span className="parcel-mode-pill">● Walk-in parcel</span></div>
      {error && <p role="alert" className="operations-error">{error}</p>}
      {message && <p role="status" className="ops-approved-message">{message}</p>}
      <form onSubmit={submit}>
        <div className="parcel-layout">
          <aside className="parcel-category-bar" aria-label="Menu categories">
            <div className="parcel-category-heading">Browse Categories</div>
            <button type="button" className={`parcel-category ${selectedCategory === "ALL" ? "active" : ""}`} aria-pressed={selectedCategory === "ALL"} onClick={() => setSelectedCategory("ALL")}>All Items <span>{menu.length}</span></button>
            {categories.map((category) => (
              <button type="button" key={category} className={`parcel-category ${selectedCategory === category ? "active" : ""}`} aria-pressed={selectedCategory === category} onClick={() => setSelectedCategory(category)}>
                {category}<span>{menu.filter((i) => i.category === category).length}</span>
              </button>
            ))}
          </aside>
          <div className="parcel-menu-area">
            <div className="parcel-menu-heading">
              <h3>{selectedCategory === "ALL" ? "All Menu Items" : selectedCategory}</h3>
              <input aria-label="Search menu" className="parcel-search" placeholder="Search menu items..." value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
            {loading ? <p>Loading menu…</p> : filtered.length === 0 ? <p className="parcel-empty">No menu items found in this category.</p> : (
              <div className="parcel-items">
                {filtered.map((i) => (
                  <div className="parcel-item" key={i.id}>
                    <div className="parcel-item-info"><strong>{i.name}</strong><small>{i.category}</small><span className="parcel-item-price">{money(Number(i.price))}</span></div>
                    <div className="parcel-quantity">
                      <button type="button" aria-label={`Remove one ${i.name}`} disabled={Number(quantities[i.id] || 0) === 0} onClick={() => changeQuantity(i.id, Number(quantities[i.id] || 0) - 1)}>−</button>
                      <input type="number" min="0" max="100" step="1" aria-label={`Quantity for ${i.name}`} value={quantities[i.id] ?? 0} onChange={(e) => changeQuantity(i.id, e.target.value)} />
                      <button type="button" aria-label={`Add one ${i.name}`} disabled={Number(quantities[i.id] || 0) >= 100} onClick={() => changeQuantity(i.id, Number(quantities[i.id] || 0) + 1)}>+</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="parcel-checkout"><div className="parcel-summary"><span className="parcel-summary-label">ORDER SUMMARY</span><strong>{count} item{count === 1 ? "" : "s"} selected <span className="parcel-summary-total">{money(total)}</span></strong><small>Final tax and service charge are calculated securely by Django.</small></div><button type="submit" disabled={busy || !chosen.length} className="parcel-submit">{busy ? "Creating order…" : "Create Parcel Order →"}</button></div>
      </form>
    </section>
  );
}